import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FaPlus,
  FaTrash,
  FaCopy,
  FaCalendarAlt,
  FaTicketAlt,
  FaRandom,
  FaSearch,
  FaFilter,
  FaEdit,
  FaEye,
  FaSync,
  FaChartLine,
  FaUsers,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaPercent,
  FaRupeeSign,
  FaGift,
  FaExclamationTriangle,
  FaInfoCircle,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CouponManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [FormCoupon, setFormCoupon] = useState({
    code: "",
    discount: "",
    type: "percentage",
    validFrom: "",
    validUntil: "",
    usageLimit: "",
    minPurchase: "",
    status: "Active",
    description: "",
  });
  const [generatedCode, setGeneratedCode] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    // Fetch existing coupons from backend
    const fetchCoupons = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/get/all/coupons`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Fetched Coupons Data:", response);
        setCoupons(response.data || []);
      } catch (error) {
        toast.error("Failed to fetch coupons");
      } finally {
        setLoading(false);
      }
    };
    fetchCoupons();
  }, []);

  // Filter coupons based on search and filters
  const filteredCoupons = coupons.filter((coupon) => {
    if (!coupon) return false;

    const search = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm ||
      coupon.code?.toLowerCase().includes(search) ||
      coupon.discount?.toString().includes(search) ||
      coupon.type?.toLowerCase().includes(search) ||
      coupon.status?.toLowerCase().includes(search) ||
      coupon.description?.toLowerCase().includes(search);

    const matchesStatus = statusFilter === "all" || coupon.status?.toLowerCase() === statusFilter.toLowerCase();
    const matchesType = typeFilter === "all" || coupon.type?.toLowerCase() === typeFilter.toLowerCase();

    return matchesSearch && matchesStatus && matchesType;
  });

  // Calculate statistics
  const stats = {
    total: coupons.length,
    active: coupons.filter(c => String(c.status).toLowerCase() === 'active').length,
    inactive: coupons.filter(c => String(c.status).toLowerCase() === 'inactive').length,
    percentage: coupons.filter(c => String(c.type).toLowerCase() === 'percentage').length,
    fixed: coupons.filter(c => String(c.type).toLowerCase() === 'fixed' || String(c.type).toLowerCase() === 'flat').length,
  };



  // Delete coupon by id
  const handleDeleteCoupon = async (id) => {
    if (!window.confirm("Are you sure you want to delete this coupon?")) return;
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/delete/coupon/${id}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response.status === 200) {
        toast.success("Coupon deleted successfully!");
        setCoupons(coupons.filter(c => c._id !== id));
      }
    } catch (error) {
      toast.error("Failed to delete coupon");
    }
  };

  const handleToggleStatus = async (coupon) => {
    const newStatus = coupon.status?.toLowerCase() === "active" ? "inactive" : "active";
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/update/coupon/${coupon._id}`,
        { status: newStatus },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response.status === 200) {
        toast.success(`Coupon ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully!`);
        setCoupons(coupons.map(c => c._id === coupon._id ? { ...c, status: newStatus.charAt(0).toUpperCase() + newStatus.slice(1) } : c));
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleChange = (field, value) => {
    setFormCoupon((prev) => ({ ...prev, [field]: value }));
  };

  const handleGenerate = () => {
    const prefix = FormCoupon.code || "";
    const minLength = 10;
    let randomLength = Math.max(minLength - prefix.length, 0);
    let randomPart = Math.random()
      .toString(36)
      .substring(2, 2 + randomLength)
      .toUpperCase();

    // If randomPart is too short (because Math.random() can be short), pad it
    while (randomPart.length < randomLength) {
      randomPart += Math.random()
        .toString(36)
        .substring(2, 2 + (randomLength - randomPart.length))
        .toUpperCase();
    }

    const code = (prefix + randomPart).substring(
      0,
      Math.max(prefix.length, minLength)
    );
    setGeneratedCode(code);
    handleChange("code", code);
  };

  const handleDuplicate = (coupon) => {
    setFormCoupon({
      code: `${coupon.code}_COPY`,
      discount: coupon.discount,
      type: coupon.type,
      validFrom: coupon.validFrom ? new Date(coupon.validFrom).toISOString().split('T')[0] : "",
      validUntil: coupon.validUntil ? new Date(coupon.validUntil).toISOString().split('T')[0] : "",
      usageLimit: coupon.usageLimit || "",
      minPurchase: coupon.minPurchase || "",
      status: "Active",
      description: coupon.description || "",
    });
    setEditingId(null);
    setIsEditMode(false);
    setShowAddModal(true);
  };

  const validateForm = () => {
    if (!FormCoupon.code) return "Coupon code is required";
    if (!FormCoupon.discount || FormCoupon.discount <= 0) return "Valid discount value is required";
    if (!FormCoupon.validFrom) return "Start date is required";
    if (!FormCoupon.validUntil) return "End date is required";
    if (new Date(FormCoupon.validFrom) > new Date(FormCoupon.validUntil)) return "Start date cannot be after end date";
    return null;
  };

  const handleEditClick = (coupon) => {
    setFormCoupon({
      code: coupon.code,
      discount: coupon.discount,
      type: coupon.type,
      validFrom: coupon.validFrom ? new Date(coupon.validFrom).toISOString().split('T')[0] : "",
      validUntil: coupon.validUntil ? new Date(coupon.validUntil).toISOString().split('T')[0] : "",
      usageLimit: coupon.usageLimit || "",
      minPurchase: coupon.minPurchase || "",
      status: coupon.status,
      description: coupon.description || "",
    });
    setEditingId(coupon._id);
    setIsEditMode(true);
    setShowAddModal(true);
  };

  const handleSubmit = async () => {
    const error = validateForm();
    if (error) {
      toast.error(error);
      return;
    }

    try {
      setLoading(true);
      const url = isEditMode
        ? `${process.env.REACT_APP_API_BASE_URL}/update/coupon/${editingId}`
        : `${process.env.REACT_APP_API_BASE_URL}/genrate/coupon`;

      const method = isEditMode ? 'put' : 'post';

      const response = await axios[method](
        url,
        FormCoupon,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast.success(`Coupon ${isEditMode ? 'updated' : 'added'} successfully!`);
      setShowAddModal(false);
      setIsEditMode(false);
      setEditingId(null);
      setGeneratedCode("");

      // Update local state instead of reload
      if (isEditMode) {
        setCoupons(coupons.map(c => c._id === editingId ? response.data.coupon : c));
      } else {
        setCoupons([response.data.coupon, ...coupons]);
      }

      setFormCoupon({
        code: "",
        discount: "",
        type: "percentage",
        validFrom: "",
        validUntil: "",
        usageLimit: "",
        minPurchase: "",
        status: "Active",
        description: "",
      });
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'add'} coupon`);
      console.error("Error saving coupon:", error);
    } finally {
      setLoading(false);
    }
  };

  // const handleDeleteCoupon = (id) => {
  //   setCoupons(coupons.filter((c) => c.id !== id));
  //   toast.info("Coupon deleted");
  // };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Coupon code copied!");
  };

  // Function to format date to show only day, month, year
  // Pagination logic
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const totalPages = Math.ceil(filteredCoupons.length / rowsPerPage);
  const paginatedCoupons = Array.isArray(filteredCoupons)
    ? filteredCoupons.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
    : [];
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, typeFilter]);
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header Section */}
      <div className="bg-gradient-to-r from-purple-700 via-indigo-600 to-blue-600 shadow-xl overflow-hidden relative">
        <div className="absolute inset-0 bg-white/5 backdrop-blur-3xl"></div>
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse delay-700"></div>

        <div className="px-4 sm:px-6 lg:px-8 py-10 relative z-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div className="flex items-center">
              <div className="p-4 bg-white/20 backdrop-blur-md rounded-2xl shadow-inner border border-white/30">
                <FaGift className="h-10 w-10 text-white drop-shadow-lg" />
              </div>
              <div className="ml-6">
                <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight drop-shadow-md">
                  Coupon Management
                </h1>
                <p className="text-purple-100 text-lg sm:text-xl font-medium opacity-90">
                  Design, monitor, and scale your promotional campaigns
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  setIsEditMode(false);
                  setFormCoupon({
                    code: "",
                    discount: "",
                    type: "percentage",
                    validFrom: "",
                    validUntil: "",
                    usageLimit: "",
                    minPurchase: "",
                    status: "Active",
                    description: "",
                  });
                  setShowAddModal(true);
                }}
                className="group inline-flex items-center px-6 py-3 bg-white text-purple-600 rounded-xl hover:bg-purple-50 transition-all duration-300 shadow-[0_10px_20px_-5px_rgba(0,0,0,0.2)] hover:shadow-[0_15px_25px_-5px_rgba(0,0,0,0.3)] hover:-translate-y-1 font-bold text-lg"
              >
                <FaPlus className="mr-3 group-hover:rotate-90 transition-transform duration-300" />
                Create Coupon
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaChartLine className="h-8 w-8 text-blue-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Coupons</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaCheckCircle className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Active</p>
                <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaTimesCircle className="h-8 w-8 text-red-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Inactive</p>
                <p className="text-2xl font-bold text-gray-900">{stats.inactive}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-yellow-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaPercent className="h-8 w-8 text-yellow-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Percentage</p>
                <p className="text-2xl font-bold text-gray-900">{stats.percentage}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-indigo-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaRupeeSign className="h-8 w-8 text-indigo-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Fixed Amount</p>
                <p className="text-2xl font-bold text-gray-900">{stats.fixed}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search coupons by code, discount, type, status..."
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="lg:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Type Filter */}
            <div className="lg:w-48">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="all">All Types</option>
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>

            {/* Refresh Button */}
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <FaSync className="mr-2" />
              Refresh
            </button>
          </div>
        </div>

        {/* Coupons Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center p-8">
              <FaSync className="animate-spin text-purple-500 text-3xl mr-3" />
              <span className="text-gray-600">Loading coupons...</span>
            </div>
          ) : filteredCoupons.length === 0 ? (
            <div className="text-center p-8">
              <FaTicketAlt className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No coupons found</h3>
              <p className="text-gray-500 mb-4">
                {searchTerm || statusFilter !== "all" || typeFilter !== "all"
                  ? "Try adjusting your search or filter criteria."
                  : "Get started by creating your first coupon."}
              </p>
              {!searchTerm && statusFilter === "all" && typeFilter === "all" && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  <FaPlus className="mr-2" />
                  Create First Coupon
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-purple-600 to-indigo-600">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaTicketAlt className="w-4 h-4" />
                          Coupon Code
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaPercent className="w-4 h-4" />
                          Discount
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaCalendarAlt className="w-4 h-4" />
                          Validity
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaCheckCircle className="w-4 h-4" />
                          Status
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaUsers className="w-4 h-4" />
                          Usage
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaEye className="w-4 h-4" />
                          Actions
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedCoupons.map((coupon) => (
                      <tr key={coupon._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <FaTicketAlt className="text-purple-500 mr-3 flex-shrink-0" />
                            <div>
                              <div className="text-sm font-medium text-gray-900 font-mono">
                                {coupon.code}
                              </div>
                              <div className="text-xs text-gray-500">
                                {coupon.description || "No description"}
                              </div>
                            </div>
                            <button
                              onClick={() => copyToClipboard(coupon.code)}
                              className="ml-3 text-gray-400 hover:text-purple-600 transition-colors"
                              title="Copy code"
                            >
                              <FaCopy className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {coupon.type === "percentage" ? (
                              <FaPercent className="text-green-500 mr-2" />
                            ) : (
                              <FaRupeeSign className="text-blue-500 mr-2" />
                            )}
                            <span className="text-sm font-medium text-gray-900">
                              {coupon.discount}
                              {coupon.type === "percentage" ? "%" : "₹"}
                            </span>
                          </div>
                          {coupon.minPurchase && (
                            <div className="text-xs text-gray-500">
                              Min: {coupon.minPurchase}₹
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <FaCalendarAlt className="mr-2 text-gray-400" />
                            <div>
                              <div>{formatDate(coupon.validFrom)}</div>
                              <div className="text-xs">to {formatDate(coupon.validUntil)}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${String(coupon.status).toLowerCase() === "active"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                              }`}
                          >
                            {String(coupon.status).toLowerCase() === "active" ? (
                              <FaCheckCircle className="w-3 h-3 mr-1" />
                            ) : (
                              <FaTimesCircle className="w-3 h-3 mr-1" />
                            )}
                            {coupon.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <FaUsers className="mr-2 text-gray-400" />
                            <span className="font-medium">
                              {coupon.usedCount || 0} / {coupon.usageLimit || "∞"}
                            </span>
                          </div>
                          {coupon.usageLimit && (
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                              <div
                                className="bg-purple-600 h-2 rounded-full"
                                style={{
                                  width: `${Math.min(
                                    ((coupon.usedCount || 0) / coupon.usageLimit) * 100,
                                    100
                                  )}%`,
                                }}
                              ></div>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => handleToggleStatus(coupon)}
                              className={`p-2 rounded-lg transition-all duration-200 ${coupon.status?.toLowerCase() === 'active'
                                ? 'bg-amber-100 text-amber-600 hover:bg-amber-200'
                                : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
                                }`}
                              title={coupon.status?.toLowerCase() === 'active' ? "Deactivate" : "Activate"}
                            >
                              <FaSync className={`w-4 h-4 ${coupon.status?.toLowerCase() === 'active' ? 'rotate-180' : ''}`} />
                            </button>
                            <button
                              onClick={() => handleDuplicate(coupon)}
                              className="p-2 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 transition-all duration-200"
                              title="Duplicate coupon"
                            >
                              <FaCopy className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditClick(coupon)}
                              className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-all duration-200"
                              title="Edit coupon"
                            >
                              <FaEdit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteCoupon(coupon._id)}
                              className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all duration-200"
                              title="Delete coupon"
                            >
                              <FaTrash className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden">
                {paginatedCoupons.map((coupon) => (
                  <div key={coupon._id} className="border-b border-gray-200 p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <FaTicketAlt className="text-purple-500 mr-2" />
                          <span className="font-mono font-medium text-gray-900">
                            {coupon.code}
                          </span>
                          <button
                            onClick={() => copyToClipboard(coupon.code)}
                            className="ml-2 text-gray-400 hover:text-purple-600"
                          >
                            <FaCopy className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Discount:</span>
                            <div className="font-medium">
                              {coupon.discount}
                              {coupon.type === "percentage" ? "%" : "₹"}
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-500">Status:</span>
                            <div>
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${String(coupon.status).toLowerCase() === "active"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                                  }`}
                              >
                                {coupon.status}
                              </span>
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-500">Validity:</span>
                            <div className="text-xs">
                              {formatDate(coupon.validFrom)} - {formatDate(coupon.validUntil)}
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-500">Usage:</span>
                            <div className="text-xs">
                              {coupon.usedCount || 0} / {coupon.usageLimit || "∞"}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="ml-4 flex flex-col gap-2">
                        <button
                          onClick={() => handleToggleStatus(coupon)}
                          className={`p-2 rounded-lg transition-all ${coupon.status?.toLowerCase() === 'active'
                            ? 'bg-amber-100 text-amber-600'
                            : 'bg-emerald-100 text-emerald-600'
                            }`}
                        >
                          <FaSync className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditClick(coupon)}
                          className="p-2 bg-blue-100 text-blue-600 rounded-lg"
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteCoupon(coupon._id)}
                          className="p-2 bg-red-100 text-red-600 rounded-lg"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row items-center justify-between">
                    <div className="mb-4 sm:mb-0">
                      <p className="text-sm text-gray-700">
                        Showing{" "}
                        <span className="font-medium">
                          {((currentPage - 1) * rowsPerPage) + 1}
                        </span>{" "}
                        to{" "}
                        <span className="font-medium">
                          {Math.min(currentPage * rowsPerPage, filteredCoupons.length)}
                        </span>{" "}
                        of <span className="font-medium">{filteredCoupons.length}</span> results
                      </p>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${currentPage === 1
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                          }`}
                      >
                        Previous
                      </button>

                      <div className="flex space-x-1">
                        {[...Array(totalPages).keys()].map((number) => (
                          <button
                            key={number + 1}
                            onClick={() => handlePageChange(number + 1)}
                            className={`px-3 py-2 text-sm font-medium rounded-md ${currentPage === number + 1
                              ? "bg-purple-600 text-white"
                              : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                              }`}
                          >
                            {number + 1}
                          </button>
                        ))}
                      </div>

                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${currentPage === totalPages
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                          }`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Add Coupon Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-indigo-50">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-indigo-600">
                  {isEditMode ? "Edit Coupon" : "Create New Coupon"}
                </h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all duration-200"
                >
                  <FaTimesCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="px-6 py-4">
              <div className="space-y-6">
                {/* Coupon Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Coupon Code *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={FormCoupon.code}
                      onChange={(e) => handleChange("code", e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="Enter coupon code"
                      required
                    />
                    <button
                      type="button"
                      onClick={handleGenerate}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                      title="Generate random code"
                    >
                      <FaRandom />
                    </button>
                  </div>
                  {generatedCode && (
                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-700">
                        Generated Code: <span className="font-mono font-bold">{generatedCode}</span>
                      </p>
                    </div>
                  )}
                </div>

                {/* Discount and Type */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Discount Value *
                    </label>
                    <input
                      type="number"
                      value={FormCoupon.discount}
                      onChange={(e) => handleChange("discount", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="Enter discount value"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Discount Type *
                    </label>
                    <select
                      value={FormCoupon.type}
                      onChange={(e) => handleChange("type", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount (₹)</option>
                    </select>
                  </div>
                </div>

                {/* Validity Dates */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valid From *
                    </label>
                    <input
                      type="date"
                      value={FormCoupon.validFrom}
                      onChange={(e) => handleChange("validFrom", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valid Until *
                    </label>
                    <input
                      type="date"
                      value={FormCoupon.validUntil}
                      onChange={(e) => handleChange("validUntil", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      required
                    />
                  </div>
                </div>

                {/* Usage Limit and Min Purchase */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Usage Limit
                    </label>
                    <input
                      type="number"
                      value={FormCoupon.usageLimit}
                      onChange={(e) => handleChange("usageLimit", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="Leave empty for unlimited"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Minimum Purchase (₹)
                    </label>
                    <input
                      type="number"
                      value={FormCoupon.minPurchase}
                      onChange={(e) => handleChange("minPurchase", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="Minimum purchase amount"
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={FormCoupon.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Enter coupon description (optional)"
                  />
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 shadow-md transform hover:-translate-y-0.5 active:scale-95 disabled:opacity-50 disabled:pointer-events-none flex items-center"
              >
                {loading && <FaSync className="animate-spin mr-2" />}
                {isEditMode ? "Update Coupon" : "Create Coupon"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default CouponManagement;
